import type { LoginPayload } from '@yana/shared';
import { api } from '@/api/client';

type Identity = { privateKey: CryptoKey; publicKey: NonNullable<LoginPayload['browserProof']>['publicKey']; keyId: string };
let pending: Promise<Identity> | undefined;
const base64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

async function readIdentity(): Promise<Identity> {
  if (!crypto.subtle || !globalThis.indexedDB) throw new Error('Браузер не поддерживает защищённый вход. Откройте сайт по HTTPS в обычном режиме браузера.');
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('yana-browser-access', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('keys');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('Не удалось открыть хранилище браузера. Разрешите сохранение данных сайта.'));
  });
  try {
    const read = () => new Promise<Identity | undefined>((resolve, reject) => {
      const request = db.transaction('keys').objectStore('keys').get('identity');
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    const saved = await read();
    if (saved) return saved;
    const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
    const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    const publicKey = { kty: 'EC', crv: 'P-256', x: jwk.x!, y: jwk.y! } as const;
    const keyId = base64(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(publicKey)))));
    const identity = { privateKey: pair.privateKey, publicKey, keyId };
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('keys', 'readwrite');
      const request = tx.objectStore('keys').add(identity, 'identity');
      // Другая вкладка могла успеть первой. Читаем победивший ключ ниже.
      request.onerror = event => {
        if (request.error?.name === 'ConstraintError') event.preventDefault();
      };
      tx.oncomplete = () => resolve();
      tx.onabort = () => reject(new Error('Не удалось сохранить ключ браузера. Разрешите сохранение данных сайта.'));
    });
    return (await read())!;
  } finally { db.close(); }
}

export function browserIdentity(): Promise<Identity> {
  // Следующее обращение перечитывает IndexedDB: очистка данных сайта
  // должна сменить ключ даже в уже открытой вкладке.
  pending ??= readIdentity().finally(() => { pending = undefined; });
  return pending;
}
export async function browserLoginProof(login: string): Promise<LoginPayload['browserProof']> {
  const identity = await browserIdentity();
  const { challengeId, challenge } = await api.post<{ challengeId: string; challenge: string }>('/session/browser-challenge', { login, keyId: identity.keyId });
  const signature = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, identity.privateKey, new TextEncoder().encode(challenge));
  return { challengeId, publicKey: identity.publicKey, signature: base64(new Uint8Array(signature)) };
}
